import {flow, makeObservable, observable} from "mobx";

import {EAIClient} from "Services/EAIClient";
import {appStore} from "../";
import {TableParams} from "Stores/commonStore";

interface DefaultResult {
    totalCount: number
}

export interface BannerSchool {
    ruuid: string
    school_id: string
    api_banner_ruuid: string
}

export interface DataSourceToBanner {
    ruuid: string
    banner_ruuid: string
    datasource: string
}

export interface UseToBanner {
    ruuid: string
    banner_ruuid: string
    user_ruuid: string
}

export interface SchoolTypesToBanner {
    ruuid: string
    banner_ruuid: string
    school_type: string
}

export interface Banner {
    ruuid: string
    school_id: string | null
    banner_text: string
    description: string | null
    color: string
    background_color: string
    links_color: string
    start_date: string | null
    end_date: string | null
    admins: boolean | null
    enabled: boolean
    enflux_created?: string
    schools: BannerSchool[]
    data_sources: DataSourceToBanner[]
    users: UseToBanner[]
    school_types: SchoolTypesToBanner[]
    notification_type: string
    like_button: boolean
    remind_me_later: boolean
    cta: boolean
    cta_text: string
    cta_url: string
    users_type: string
    created_by_name?: string
}

export interface BannersDataQuery {
    data: Banner[]
    result: DefaultResult
}
export interface BannersByYear {
    [year: number]: Banner[];
}

interface RemindMeLaterItem {
    value: string
    expireAt: number
}

class BannerStore {
    @observable banners: Banner[] = [];
    @observable bannersManagement: Banner[] = [];
    @observable totalBannersManagement = 0;
    @observable isFetchingBanners = false;
    @observable whatsNewBanners: BannersByYear = [];
    @observable isFetchingBannersManagement = false;
    @observable isUpdatingBanner = false;
    @observable isAddingBanner = false;

    client = new EAIClient();

    constructor() {
        makeObservable(this);
    }

    fetchBannersManagement = flow(function *(this: BannerStore, params: TableParams) {
        this.isFetchingBannersManagement = true;
        try {
            const bannersResp = yield this.client.fetchBannersManagement(params);
            this.totalBannersManagement = bannersResp.data.result.total_count;
            this.bannersManagement = bannersResp.data.data as Banner[];
            return this.bannersManagement;
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isFetchingBannersManagement = false;
        }
    });

    fetchBannerSchools = flow(function *(this: BannerStore, ruuid: string) {
       try {
           const bannersResp = yield this.client.fetchBannerSchools(ruuid);
           return bannersResp.data.data as string[];
       } catch (e) {
           appStore.addError(e);
       }
    });

    deleteBanner = flow(function *(this: BannerStore, ruuid: string) {
       try {
           yield this.client.deleteBanner(ruuid);
       } catch (e) {
           appStore.addError(e);
       }
    });

    updateBanner = flow(function *(this: BannerStore, ruuid: string, banner: Banner, schoolIds: string[], schoolTypes: string[], dataSources: string[]) {
        this.isUpdatingBanner = true;
        try {
            yield this.client.updateBanner(ruuid, banner, schoolIds, schoolTypes, dataSources);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isUpdatingBanner = false;
        }
    });

    addBanner = flow(function *(this: BannerStore, banner: Banner, schoolIds: string[], schoolTypes: string[], dataSources: string[]) {
        this.isAddingBanner = true;
        try {
            yield this.client.addBanner(banner, schoolIds, schoolTypes, dataSources);
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isAddingBanner = false;
        }
    });

    @flow
    *fetchBanners(): any {
        this.isFetchingBanners = true;
        try {
            const bannersResp = yield this.client.fetchBanners();
            const banners = bannersResp.data.data as Banner[];
            const dismissedBannersStr = localStorage.getItem("dismissedBanners");
            const dismissedBanners = dismissedBannersStr && JSON.parse(dismissedBannersStr) || [];
            const filteredRemindMeLater = this.getRemindMeLater();
            this.banners = banners.filter(banner => !dismissedBanners.includes(banner.ruuid) && !filteredRemindMeLater.some((item) => item.value === banner.ruuid));
            return this.banners;
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isFetchingBanners = false;
        }
    }

    @flow
    *fetchBannersBySubdomain(subdomain: string): any {
        this.isFetchingBanners = true;
        try {
            const bannersResp = yield this.client.fetchBannersBySubdomain(subdomain);
            const banners = bannersResp.data.data as Banner[];
            const dismissedBannersStr = localStorage.getItem("dismissedBanners");
            const dismissedBanners = dismissedBannersStr && JSON.parse(dismissedBannersStr) || [];
            this.banners = banners.filter(banner => !dismissedBanners.includes(banner.ruuid));
            return this.banners;
        } catch (e) {
            appStore.addError(e);
        } finally {
            this.isFetchingBanners = false;
        }
    }

    @flow
    *dismissBanner(bannerRuuid: string) {
        const dismissedBannersStr = localStorage.getItem("dismissedBanners");
        const dismissedBanners = dismissedBannersStr && JSON.parse(dismissedBannersStr) || [];
        dismissedBanners.push(bannerRuuid);
        localStorage.setItem("dismissedBanners", JSON.stringify(dismissedBanners));
        this.banners = this.banners.filter(banner => banner.ruuid !== bannerRuuid);
    }

    likeBanner = flow(function *(this: BannerStore, ruuid: string) {
        try {
            yield this.client.likeBanner(ruuid);
        } catch (e) {
            appStore.addError(e);
        }
    });

    remindMeLater = flow(function *(this: BannerStore, ruuid: string) {
        const remindMeLaterStr = localStorage.getItem("remindMeLater");
        const remindMeLater = remindMeLaterStr && JSON.parse(remindMeLaterStr) || [];
        const item = {
            value: ruuid,
            expireAt: new Date().getTime() + 24 * 60 * 60 * 1000
        };
        remindMeLater.push(item);
        localStorage.setItem("remindMeLater", JSON.stringify(remindMeLater));
        this.fetchBanners();
    });

    getRemindMeLater = () => {
        const remindMeLaterStr = localStorage.getItem("remindMeLater");
        const remindMeLater = remindMeLaterStr && JSON.parse(remindMeLaterStr) || [];
        const now = new Date();
        const filteredRemindMeLater = remindMeLater.filter((item: any) => item.expireAt > now.getTime());
        if (filteredRemindMeLater.length !== remindMeLater.length) {
            localStorage.setItem("remindMeLater", JSON.stringify(filteredRemindMeLater));
        }
        return filteredRemindMeLater as RemindMeLaterItem[];
    };

    @flow
    *fetchWhatsNewBanners(): any {
        this.isFetchingBanners = true
        try {
            const bannerResponse = yield this.client.fetchWhatsNewBanner()
            this.whatsNewBanners = bannerResponse.data.data as BannersByYear;
            return this.whatsNewBanners
        } catch (e) {
            appStore.addError(e)
        }finally {
            this.isFetchingBanners = false;
        }
    }
}

export { BannerStore };
